import type React from "react";

/**
 * A rule for replacing text patterns with React components.
 * The pattern should use the global flag (g) to find all matches.
 */
export interface ReplacementRule {
  /**
   * Regex pattern to match in the content.
   * Must use the global flag (g) to find all matches.
   * Use capture groups to extract data for the render function.
   */
  pattern: RegExp;
  /**
   * Render function that receives the regex match and returns a React element.
   * @param match - The RegExpMatchArray from the pattern match
   * @param index - A unique index for keying the element
   */
  render: (match: RegExpMatchArray, index: number) => React.ReactElement;
}

/**
 * Processes text and replaces pattern matches with React components.
 * Returns an array of strings and React elements.
 *
 * @param text - The text to process
 * @param rules - Array of replacement rules to apply
 * @returns Array of strings and React elements
 */
export const processReplacements = (text: string, rules: ReplacementRule[]): (string | React.ReactElement)[] => {
  if (!rules.length) {
    return [text];
  }

  // Track all matches with their positions
  interface MatchInfo {
    match: RegExpMatchArray;
    rule: ReplacementRule;
    startIndex: number;
    endIndex: number;
    ruleIndex: number;
    matchIndex: number;
  }

  const allMatches: MatchInfo[] = [];

  // Find all matches from all rules
  for (let ruleIndex = 0; ruleIndex < rules.length; ruleIndex++) {
    const rule = rules[ruleIndex];

    // Validate that the pattern has the global flag to prevent infinite loops
    if (!rule.pattern.global) {
      console.warn(
        `[MarkdownRenderer] Replacement pattern "${rule.pattern.source}" is missing the global flag (g). ` +
          "This would cause an infinite loop. Skipping this rule.",
      );
      continue;
    }

    // Reset lastIndex to ensure we start from the beginning
    rule.pattern.lastIndex = 0;

    let match: RegExpExecArray | null = rule.pattern.exec(text);
    let matchIndex = 0;

    while (match !== null) {
      allMatches.push({
        match: [...match] as RegExpMatchArray,
        rule,
        startIndex: match.index,
        endIndex: match.index + match[0].length,
        ruleIndex,
        matchIndex,
      });
      matchIndex++;
      match = rule.pattern.exec(text);
    }
  }

  // Sort matches by start position (earlier matches first)
  // If same position, use rule order (lower ruleIndex takes precedence)
  allMatches.sort((a, b) => {
    if (a.startIndex !== b.startIndex) {
      return a.startIndex - b.startIndex;
    }
    return a.ruleIndex - b.ruleIndex;
  });

  // Filter out overlapping matches (keep the first one at each position)
  const nonOverlappingMatches: MatchInfo[] = [];
  let lastEndIndex = 0;

  for (const matchInfo of allMatches) {
    if (matchInfo.startIndex >= lastEndIndex) {
      nonOverlappingMatches.push(matchInfo);
      lastEndIndex = matchInfo.endIndex;
    }
  }

  // Build result array
  const parts: (string | React.ReactElement)[] = [];
  let currentIndex = 0;
  let componentIndex = 0;

  for (const matchInfo of nonOverlappingMatches) {
    // Add text before this match
    if (currentIndex < matchInfo.startIndex) {
      parts.push(text.slice(currentIndex, matchInfo.startIndex));
    }

    // Add the rendered component
    parts.push(matchInfo.rule.render(matchInfo.match, componentIndex));
    componentIndex++;

    currentIndex = matchInfo.endIndex;
  }

  // Add remaining text after last match
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  return parts.length > 0 ? parts : [text];
};
